import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Gamepad2 } from "lucide-react";

/**
 * GamesQuickLinkFab — persistent bottom-right shortcut to the Games
 * Arcade. Visible on every page EXCEPT the arcade itself and any
 * individual game route (kids don't need a "Games" button while
 * they're playing). Kept small and out of the way so it doesn't
 * cover core UI on tablets.
 */
export default function GamesQuickLinkFab() {
  const { pathname } = useLocation();
  // Hide on the arcade itself and on any nested /games/* route or any
  // top-level individual-game slug. Keeps the FAB from covering the
  // game canvas.
  const HIDDEN_PREFIXES = [
    "/games", "/dodge", "/first-quest", "/code-breaker", "/treasure-quest",
    "/tic-tac-toe", "/name-place-animal", "/kaleidoscope", "/pixel-art",
    "/stained-glass", "/mandala", "/squishy", "/snap", "/quiz",
    "/puzzles", "/spelling-bee", "/spell",
    "/games/duel",  // 2-player duels — never overlay the split screen
    "/student/", // student portal is play-only; keep it clean
  ];
  const hidden = HIDDEN_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
  if (hidden) return null;

  return (
    <Link
      to="/games"
      data-testid="games-fab"
      aria-label="Open the Games Arcade"
      title="Games Arcade"
      // Feb 2026 (Cycle 22F): the mobile bottom tabbar owns the
      // bottom-right corner, so the FAB is hidden below the `lg`
      // breakpoint. Desktop/tablet users still get the shortcut.
      className="hidden lg:flex fixed z-40 bottom-6 right-6 h-16 w-16 rounded-full border-[3px] border-slate-900 bg-yellow-300 shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:-translate-y-[2px] hover:shadow-[0_6px_0_0_rgba(15,23,42,1)] active:translate-y-[1px] transition-all items-center justify-center group"
    >
      <Gamepad2 size={26} strokeWidth={3} className="text-slate-900" />
      <span className="pointer-events-none absolute -left-2 top-1/2 -translate-y-1/2 -translate-x-full opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap px-3 py-1 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hidden md:inline-block">
        Play games
      </span>
      {/* Subtle red dot indicator hinting there are unplayed games */}
      <span className="absolute top-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-slate-900 bg-rose-500" aria-hidden />
    </Link>
  );
}
